import { SearchBar } from '@/shared/components/search';
import { HOME_SEARCH_DEFAULT_MODE } from '@letsgethome/lib/constants';
import { urlModeFor } from '@letsgethome/routes';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';

import { useHeroSearchPlaceholder } from './useRotatingPlaceholder';

/**
 * DockableHeroSearch - React Portal Pattern (Unicorn Standard)
 *
 * ONE SearchBar instance that teleports via React Portal. When docked,
 * the SearchBar is portaled into #navbar-portal-slot in the NavbarShell.
 *
 * Why Portal pattern (upgraded from CSS morphing):
 * - Same React state = typed text preserved
 * - Same DOM element = focus preserved while scrolling
 * - Keyboard stays open on mobile
 * - IME composition preserved (Chinese input)
 * - True DOM teleportation = perfect navbar integration
 * - No z-index hacks = cleaner stacking context
 *
 * Uses IntersectionObserver for 60fps performance (no scroll listener).
 */

// Subscribe to DOM changes (noop - we just need the SSR pattern)
const subscribe = () => () => {};

// Get portal container on client
const getPortalContainer = () => document.getElementById('navbar-portal-slot');

// SSR snapshot (null since document doesn't exist)
const getServerSnapshot = () => null;

export function DockableHeroSearch() {
  const [isDocked, setIsDocked] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [placeholderHeight, setPlaceholderHeight] = useState(0);

  // Query is owned HERE — above the element that teleports between the hero and
  // the navbar — so the typed text survives the dock (the SearchBar below
  // remounts across the portal boundary; this state does not). Focus is not
  // preserved, which is fine: the panel has already closed on the scroll that
  // triggers the dock.
  const [query, setQuery] = useState('');
  // The rotating example (M-3), shared with the mobile hero so the two can
  // never show different examples.
  const placeholder = useHeroSearchPlaceholder(query);

  // Find portal target using useSyncExternalStore (React Compiler safe)
  const portalContainer = useSyncExternalStore(subscribe, getPortalContainer, getServerSnapshot);

  // Measure search bar height for placeholder (prevents CLS when portaled)
  useEffect(() => {
    if (!containerRef.current) return;
    setPlaceholderHeight(containerRef.current.getBoundingClientRect().height);
  }, []);

  // Observe sentinel to trigger dock/undock
  useEffect(() => {
    const sentinel = document.getElementById('hero-sentinel');
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) setIsDocked(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // The SearchBar content - always the same element, just moves location
  const searchBarContent = (
    <div
      ref={!isDocked ? containerRef : undefined}
      className={isDocked ? 'w-full max-w-md' : 'w-full'}
    >
      <SearchBar
        variant={isDocked ? 'navbar' : 'hero'}
        // Home has no mode of its own: the ONE declared default (projected
        // from `domain_core::search::HOME_SEARCH_DEFAULT_MODE`), never a
        // private "rent" (epoch 44).
        mode={urlModeFor(HOME_SEARCH_DEFAULT_MODE)}
        autoFocus={false}
        value={query}
        onValueChange={setQuery}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div className="w-full">
      {/* Ghost placeholder - maintains layout space when search bar is portaled */}
      <div style={{ height: isDocked ? placeholderHeight : 'auto' }} aria-hidden={isDocked}>
        {/* When NOT docked: render in-place in hero */}
        {!isDocked && searchBarContent}
      </div>

      {/* When docked AND portal container exists: teleport to navbar */}
      {isDocked && portalContainer && createPortal(searchBarContent, portalContainer)}
    </div>
  );
}
